<script setup lang="ts">
  import { Money3Component as InputMoney } from 'v-money3'
  import { computed } from 'vue'
  import { randomId } from '@/functions'

  const props = defineProps<{
    id?: string,
    modelValue: number,
    min?: number,
    label?: string,
    required?: boolean,
    disabled?: boolean,
    placeholder?: string,
    error?: string[],
    prefix?: string,
    suffix?: string,
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void,
  }>()

  // https://github.com/jonathanpmartins/v-money3#properties
  const options = computed(() => ({
    decimal: ',',
    thousands: '.',
    precision: 0,
    min: props.min ?? 0,
  }))

  const textInput = computed({
    set: (val) => {
      emit('update:modelValue', Number(val))
    },
    get: () => props.modelValue,
  })

  const defaultId = randomId()
  const inputId = computed(() => props.id ?? defaultId)
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-red-600">
        *
      </span>
    </label>
    <div class="relative flex items-center">
      <input-money
        v-bind="options"
        :id="inputId"
        v-model="textInput"
        v-select-on-focus
        :disabled="disabled"
        class="form-input" />
      <span
        v-if="prefix"
        class="prefix">
        {{ prefix }}
      </span>
      <span
        v-if="suffix"
        class="suffix">
        {{ suffix }}
      </span>
    </div>
    <app-error-msg :msg="error" />
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .prefix {
    @apply absolute left-4 text-gray-400 pointer-events-none text-sm;
  }
  .input-has-text .prefix.suffix {
    @apply inline;
  }

  .suffix {
    @apply absolute right-4 text-gray-400 pointer-events-none text-sm;
  }

  .form-input:has(+ span.prefix) {
    @apply pl-10;
  }
  .form-input:has(+ span.suffix) {
    @apply pr-10;
  }
</style>
